import { MASTER_PRINT_TYPE } from "./designerUtils";
import {
    TECHNIQUE_SWITCH_THRESHOLD_CM,
    getPredesignTargetSizeCm,
    getSelectionEffectiveDimensionsCm,
    isFixedPredesignSelection,
    normalizePredesignSizeTier,
} from "./designerHelpers";

const PRINT_KIND = Object.freeze({
    TEXT: "text",
    PREDESIGN: "predesign",
    IMAGE_UPLOAD: "image_upload",
});
const TEXT_SAFE_MIN_LIMIT = 5;
const TEXT_SAFE_FALLBACK_LIMIT = 30;

const parseOptionalNumber = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const normalizePrintKind = (selection, mode) => {
    const attrs = selection?.attrs || {};
    const explicit = String(attrs.print_kind || "").trim().toLowerCase();
    if (explicit) return explicit;
    if (mode === "text") return PRINT_KIND.TEXT;
    return PRINT_KIND.IMAGE_UPLOAD;
};

export const resolveSafeTextMaxLetters = ({ selection = null, details = null } = {}) => {
    const print = selection?.print || {};
    const attrs = selection?.attrs || {};
    const explicitKind = String(attrs.print_kind || "").trim().toLowerCase();
    const looksLikeTextKind =
        explicitKind === PRINT_KIND.TEXT
        || Number(print?.max_letters || 0) > 0;

    const rawLimit = Number(details?.max_letters ?? print?.max_letters ?? 0);
    if (!Number.isFinite(rawLimit) || rawLimit <= 0) return 0;

    if (looksLikeTextKind && rawLimit < TEXT_SAFE_MIN_LIMIT) {
        return TEXT_SAFE_FALLBACK_LIMIT;
    }

    return Math.floor(rawLimit);
};

const resolveTechniqueKey = (attrs = {}) => {
    const explicit = String(attrs.resolved_technique || "").trim().toLowerCase();
    if (explicit) return explicit;

    const type = Number(attrs.resolved_master_print_type || 0);
    if (type === MASTER_PRINT_TYPE.PATCH) return "aplique";
    if (type === MASTER_PRINT_TYPE.SERIGRAPHY) return "estampado";
    if (type === MASTER_PRINT_TYPE.EMBROIDERY) return "bordado";
    return "";
};

const isFreeformCustomSelection = (selection = null) => {
    const attrs = selection?.attrs || {};
    if (String(attrs.freeform_custom_print || "0") === "1") return true;
    const printKind = normalizePrintKind(selection);
    if (printKind !== PRINT_KIND.TEXT && printKind !== PRINT_KIND.IMAGE_UPLOAD) return false;
    const printName = String(
        selection?.print?.factory_name
        || selection?.print?.nice_name
        || selection?.print?.name
        || ""
    ).trim().toLowerCase();
    return printName === "texto personalizado" || printName === "imagen personalizada";
};

export const resolvePrintModeStrategy = (print) => {
    const maxLetters = Number(print?.max_letters || 0);
    if (maxLetters > 0) return "text";
    return "image";
};

const textModeValidationStrategy = ({ selection, details, errors }) => {
    const attrs = selection?.attrs || {};
    const isPersonalText = String(attrs.personal || "0") === "1";
    const maxLetters = resolveSafeTextMaxLetters({ selection, details });
    const rawText = attrs.text || "";
    const normalized = String(rawText).replace(/\r\n/g, "\n");
    if (!isPersonalText && !normalized.trim()) {
        errors.push("Ingresá el texto del print.");
    }
    if (maxLetters > 0 && normalized.length > maxLetters) {
        errors.push(`El texto supera el máximo de ${maxLetters} caracteres.`);
    }
};

const imageModeValidationStrategy = ({ selection, errors }) => {
    const print = selection?.print || {};
    const attrs = selection?.attrs || {};
    const hasImage = Boolean(
        attrs.image_url
        || print?.img_full
        || print?.img_url
        || print?.icon_url
        || print?.img
        || print?.icon
    );
    if (!hasImage) {
        errors.push("Cargá una imagen para el print.");
    }
};

const MODE_VALIDATION_STRATEGIES = Object.freeze({
    text: textModeValidationStrategy,
    image: imageModeValidationStrategy,
});

export const validatePrintByModeStrategy = ({ selection, details, mode, errors }) => {
    const printKind = normalizePrintKind(selection, mode);
    const attrs = selection?.attrs || {};
    const techniqueKey = resolveTechniqueKey(attrs);

    if (printKind === PRINT_KIND.IMAGE_UPLOAD) {
        imageModeValidationStrategy({ selection, details, errors });
        return;
    }

    if (printKind === PRINT_KIND.PREDESIGN) {
        if (techniqueKey && techniqueKey !== "bordado" && techniqueKey !== "estampado") {
            errors.push("Para prediseños la técnica debe ser Bordado o Estampado.");
        }
        imageModeValidationStrategy({ selection, details, errors });
        return;
    }

    const strategy = MODE_VALIDATION_STRATEGIES[mode] || imageModeValidationStrategy;
    strategy({ selection, details, errors });
};

export const validatePrintByBoundsStrategy = ({ selection, details, errors }) => {
    const print = selection?.print || {};
    const attrs = selection?.attrs || {};
    const isFixedPredesign = isFixedPredesignSelection(selection);
    const ignoreInheritedBounds = isFreeformCustomSelection(selection) && !isFixedPredesign;
    const minHeight = ignoreInheritedBounds
        ? null
        : parseOptionalNumber(details?.min_height ?? print?.min_height);
    const maxHeight = ignoreInheritedBounds
        ? null
        : parseOptionalNumber(details?.max_height ?? print?.max_height);
    const maxWidthRaw = ignoreInheritedBounds
      ? null
      : parseOptionalNumber(
          details?.max_width ?? print?.max_width ?? print?.max_ancho ?? print?.maxw,
        );
    const maxWidth =
      maxWidthRaw !== null && maxWidthRaw > 0 ? maxWidthRaw : null;
    const effectiveDims = getSelectionEffectiveDimensionsCm(selection, {
        aspectRatioOverride: attrs.rendered_aspect_ratio,
    });
    const fixedPredesignTargetHeight = isFixedPredesign
        ? getPredesignTargetSizeCm(normalizePredesignSizeTier(attrs?.predesign_size_tier))
        : null;
    const height = parseOptionalNumber(
        attrs.rendered_height_cm
        ?? (fixedPredesignTargetHeight !== null ? String(fixedPredesignTargetHeight) : null)
        ?? attrs.height
        ?? effectiveDims?.height
    );
    const width = parseOptionalNumber(
        attrs.rendered_width_cm
        ?? attrs.width
        ?? attrs.max_ancho
        ?? attrs.maxw
        ?? effectiveDims?.width
    );

    if (!isFixedPredesign) {
        if (minHeight !== null && height !== null && height < minHeight) {
            errors.push(`La altura mínima es ${minHeight} cm.`);
        }
        if (maxHeight !== null && height !== null && height > maxHeight) {
            errors.push(`La altura máxima es ${maxHeight} cm.`);
        }
        if (maxWidth !== null && width !== null && width > maxWidth) {
            errors.push(`El ancho máximo es ${maxWidth} cm.`);
        }
    }
    if (width !== null && width <= 0) {
        errors.push("El ancho debe ser mayor a 0.");
    }
    if (height !== null && height <= 0) {
        errors.push("La altura debe ser mayor a 0.");
    }
    if (attrs.limit_hit === "1") {
        errors.push("Ajustá la posición del print para que no quede en el borde.");
    }
};

export const resolvePrintTechniqueStrategy = ({ sizeCm }) => {
    const normalizedSize = Number(sizeCm || 0);
    const isPatch = normalizedSize > TECHNIQUE_SWITCH_THRESHOLD_CM;
    return {
        isPatch,
        resolvedMasterPrintType: isPatch ? MASTER_PRINT_TYPE.PATCH : MASTER_PRINT_TYPE.EMBROIDERY,
        resolvedTechnique: isPatch ? "aplique" : "bordado",
    };
};
